/**
 * 定价策略的中文解读面板。
 *
 * `PricingPolicy.payload` 是一大坨 JSON，运营看不懂也不敢动。这里把每个字段
 * 翻译成人话，标注单位与影响面。
 *
 * **翻译表是死的，payload 是活的。** 遇到没登记的字段照样展示原始键名，
 * 只是没有解释——这样加了新字段不会在面板里凭空消失。
 */

export interface FieldDoc {
  /** 中文名 */
  label: string;
  /** 一句话说明它控制什么 */
  meaning: string;
  /** 单位，如「灵力」「分」「token」 */
  unit?: string;
}

export interface SectionDoc {
  label: string;
  meaning: string;
  /** 字段路径（相对于该 section）→ 说明。`*` 匹配任意键。 */
  fields: Record<string, FieldDoc>;
}

export const POLICY_SECTIONS: Record<string, SectionDoc> = {
  powerUnitCents: {
    label: "灵力面额",
    meaning: "1 点灵力等于多少分钱。整个经济体系的换算基准。",
    fields: {},
  },
  skillPowerCost: {
    label: "各 Skill 的灵力单价",
    meaning:
      "用户每触发一次该 Skill 扣多少灵力。0 表示不扣费（系统触发的能力由平台承担）。" +
      "这是唯一权威来源，SkillDefinition 表里**不存**这个数字，避免两处分叉。",
    fields: {
      "*": { label: "", meaning: "每次调用扣除的灵力", unit: "灵力" },
    },
  },
  chatImagePowerSurcharge: {
    label: "图片消息加价",
    meaning: "在普通对话单价之上，每发送一张图片额外扣除的灵力。",
    fields: {},
  },
  grant: {
    label: "赠送规则",
    meaning: "免费获得灵力的三个口子：新用户、每日登录、看激励视频。",
    fields: {
      newUser: { label: "新用户赠送", meaning: "注册即到账，只发一次", unit: "灵力" },
      dailyLogin: {
        label: "每日登录",
        meaning: "打开产品并进入当天首次 newDay 打招呼分支，且余额低于门槛时发放",
        unit: "灵力",
      },
      dailyLoginBalanceThreshold: {
        label: "每日赠送余额门槛",
        meaning: "当前余额必须严格小于此值；等于时不赠送",
        unit: "灵力",
      },
      adReward: { label: "激励视频单次", meaning: "看完一条广告发放", unit: "灵力" },
      adDailyLimit: { label: "激励视频每日上限", meaning: "一天最多看几次", unit: "次" },
      grantHour: {
        label: "发放时点",
        meaning: "每日赠送在几点重置。当前为 0 点，与聊天 newDay 的自然日一致",
        unit: "时（Asia/Shanghai）",
      },
      timezone: { label: "时区", meaning: "所有每日周期的判定时区" },
    },
  },
  topupTiers: {
    label: "充值档位",
    meaning: "用户可选的充值金额与到账灵力。金额一律以分为单位的整数，禁止浮点。",
    fields: {
      key: { label: "档位标识", meaning: "内部键名，下单时冻结在订单上" },
      priceCents: { label: "售价", meaning: "用户实付金额", unit: "分" },
      basePower: { label: "基础到账", meaning: "按面额换算的灵力", unit: "灵力" },
      bonusPower: { label: "赠送", meaning: "额外赠送的灵力，用于做价格锚点", unit: "灵力" },
    },
  },
  dailyQuota: {
    label: "每日次数配额",
    meaning:
      "即使灵力够，某些能力每天也有次数上限。防的是成本失控与滥用，" +
      "与扣费是两套独立的闸门。",
    fields: { "*": { label: "", meaning: "每日最多调用次数", unit: "次" } },
  },
  relationshipGain: {
    label: "关系值增长",
    meaning: "羁绊与好感怎么涨。羁绊单调递增无上限，好感单次钳制在 ±10。",
    fields: {
      bondPerUserTurn: { label: "用户每轮羁绊", meaning: "用户发一条消息加多少羁绊", unit: "点" },
      bondPerCharacterTurn: { label: "角色每轮羁绊", meaning: "角色回一条加多少羁绊", unit: "点" },
      affinityDeltaMin: { label: "好感单次下限", meaning: "一次变化最多扣多少好感", unit: "点" },
      affinityDeltaMax: { label: "好感单次上限", meaning: "一次变化最多加多少好感", unit: "点" },
      scaleAffinityByLength: {
        label: "按长度加权好感",
        meaning: "旧系统按对话量放大好感，会突破 ±10，是已修复的缺陷。必须为 false",
      },
      conversationSummaryAffectsAffinity: {
        label: "会话总结影响好感",
        meaning: "会话结束后由 LLM 综合评估再调整一次好感",
      },
      fallbackAffectsRelationship: {
        label: "兜底回复影响关系",
        meaning: "兜底不是模型生成的，不应该加羁绊或改好感。必须为 false",
      },
    },
  },
  levelCurve: {
    label: "等级曲线",
    meaning: "羁绊到等级的换算。等级是派生值，存储只是缓存。",
    fields: {},
  },
  contextBudget: {
    label: "上下文预算",
    meaning:
      "每次调用模型时最多带多少 token 的上下文。这是成本的主要杠杆——" +
      "预算翻倍，单轮成本基本也翻倍。",
    fields: {
      maxInputTokens: { label: "输入上限", meaning: "单次请求的最大输入", unit: "token" },
      cacheablePrefixTokens: {
        label: "可缓存前缀",
        meaning: "固定不变的前缀长度，命中缓存后这部分大幅降价",
        unit: "token",
      },
      maxToolRounds: { label: "工具轮次上限", meaning: "防止 search→search→search 无限递归", unit: "轮" },
      maxSkillCallsPerRound: { label: "单轮 Skill 上限", meaning: "限制模型一次响应提出的 Skill 调用数", unit: "次" },
      maxSkillCallsPerRun: { label: "整轮 Skill 上限", meaning: "跨工具轮累计限制 Skill 调用数", unit: "次" },
      charsToTokensRatio: { label: "字符换算比", meaning: "中文字符数除以该值估算 token 数" },
      historyBudgetTokens: {
        label: "历史预算",
        meaning: "留给历史消息的 token。超出后触发滚动摘要折叠",
        unit: "token",
      },
    },
  },
  rateLimit: {
    label: "频率限制",
    meaning: "单位时间内的请求上限，防刷。",
    fields: { "*": { label: "", meaning: "限流阈值" } },
  },
  legacyPowerConversion: {
    label: "旧存量换算",
    meaning: "迁移时旧系统的 power 怎么折算成新系统的灵力。",
    fields: {
      ratio: { label: "换算比例", meaning: "旧 power × 该比例 = 新灵力" },
      transactionReason: { label: "流水原因", meaning: "换算产生的 ResourceTransaction 记什么原因" },
      autoMigrateOverCap: {
        label: "超额是否自动迁移",
        meaning: "旧系统有 powerChange 漏洞，超出上限的余额可能是刷出来的",
      },
    },
  },
};

/** 找不到登记时返回 null，调用方原样展示键名。 */
export function describeField(section: string, key: string): FieldDoc | null {
  const doc = POLICY_SECTIONS[section];
  if (!doc) return null;
  return doc.fields[key] ?? doc.fields["*"] ?? null;
}
